import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { UserDetailsSummary } from '../../../user-auth/models';
import { UserAuthState } from '../../../user-auth/store/user-auth.state';
import { Comment, CreateCommentRequest } from '../../models/comment/comment.model';
import { COMMENT_DATA_SERVICE, CommentDataService } from '../../services/comment/comment-data.service';

@Component({
  selector: 'ptl-comment-input',
  templateUrl: './comment-input.component.html',
  styleUrls: ['./comment-input.component.scss'],
})
export class CommentInputComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() momentId!: string;
  @Input() placeholder = 'translations.comments.actions.addComment';
  @Input() parentCommentId?: string;
  @Input() initialValue?: string;
  @Input() isEditMode = false;

  @Output() commentCreated = new EventEmitter<Comment>();
  @Output() commentUpdated = new EventEmitter<string>();
  @Output() editCancelled = new EventEmitter<void>();

  @ViewChild('commentTextarea', { static: false }) textareaRef!: ElementRef<HTMLTextAreaElement>;

  @Select(UserAuthState.userDetailsData) userDetails$: Observable<UserDetailsSummary>;

  commentControl = new FormControl('', [Validators.required, Validators.maxLength(8000)]);
  isLoading = false;
  isFocused = false;
  userInitials = '';
  userAvatarUrl = '';

  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  }

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(COMMENT_DATA_SERVICE) private commentService: CommentDataService,
    public translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.userDetails$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.userInitials = this.getInitials(user.firstName, user.lastName);
        this.userAvatarUrl = user.imageUrl;
      }
    });

    if (this.initialValue) {
      this.commentControl.setValue(this.initialValue);
    }

    this.commentControl.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(200)).subscribe(() => {
      setTimeout(() => this.adjustTextareaHeight(), 0);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  ngOnChanges(): void {
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    if (this.commentControl.invalid || this.isLoading) {
      return;
    }

    const content = this.commentControl.value?.trim();
    if (!content) {
      return;
    }

    if (this.isEditMode) {
      this.commentUpdated.emit(content);
      this.isFocused = false;
      return;
    }

    this.isLoading = true;
    const request: CreateCommentRequest = {
      content,
      momentId: this.momentId,
      parentCommentId: this.parentCommentId,
    };

    this.commentService
      .createComment(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result.isSuccess) {
            this.commentControl.setValue('');
            this.commentCreated.emit(result.value);
            this.isFocused = false;
          } else {
            console.error('Error creating comment:', result.error);
          }
        },
        error: (error) => {
          console.error('Error creating comment:', error);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  onFocus(): void {
    this.isFocused = true;
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  onEscape(): void {
    if (this.isEditMode) {
      this.editCancelled.emit();
    } else {
      this.commentControl.setValue('');
      this.isFocused = false;
    }
  }

  onEnterKey(event: KeyboardEvent): void {
    if (event.shiftKey) {
      // Allow Shift+Enter for new lines
      return;
    }

    event.preventDefault();

    this.onSubmit();
  }

  onInput(): void {
    this.adjustTextareaHeight();
  }

  private adjustTextareaHeight(): void {
    if (this.textareaRef?.nativeElement) {
      const textarea = this.textareaRef.nativeElement;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';

      // Calculate new height based on content
      const scrollHeight = textarea.scrollHeight;
      const minHeight = this.isEditMode ? 96 : 24; // 4 rows * 24px or 1 row * 24px
      const maxHeight = 200; // Maximum height before scrolling

      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }
}
